import { type AxiosRequestConfig } from 'axios'
import { useEffect, useState } from 'react'
import { useQueries } from 'react-query'

import useAuth from 'hooks/use-auth'
import { fetchQueryData, getEndpointWithTeamId } from 'hooks/use-gremlin-query'

import type {
  UseQueryOptions,
  UseQueryResult
} from 'react-query/types/react/types'

/**
 * The top-level array returned from useQueries is referentially unstable.
 * This uses the `dataUpdatedAt` property as a heuristic to compare arrays
 * to allow memoization.
 */
function useQueriesResultMemo(queryResults: UseQueryResult[]) {
  const [cachedQueryResults, setCachedQueryResults] =
    useState<UseQueryResult[]>(queryResults)

  const areArraysConsideredTheSame =
    cachedQueryResults && queryResults.length === cachedQueryResults.length
      ? queryResults.every(({ dataUpdatedAt, errorUpdatedAt }, i) => {
          const query = cachedQueryResults[i]

          return (
            dataUpdatedAt === query?.dataUpdatedAt &&
            errorUpdatedAt === query?.errorUpdatedAt
          )
        })
      : false

  useEffect(() => {
    if (areArraysConsideredTheSame) return

    setCachedQueryResults(queryResults)
  }, [areArraysConsideredTheSame, queryResults])

  return cachedQueryResults
}

/**
 * Returns results from useQueries with additional memoization - it doesn't
 * create a new array every time it's invoked.
 *
 * Note: This hook should be used with caution because it doesn't adhere to
 * our current pattern of creating bespoke hooks for each endpoint fetch.
 */
export function useGremlinQueries(
  queries: {
    endpoint: string
    options?: UseQueryOptions
    teamId?: string
  }[],
  axiosConfig: AxiosRequestConfig = {}
) {
  const { currentTeamId } = useAuth()

  const queriesMapped = queries.map((queryObj) => {
    const { endpoint, teamId, options = {} } = queryObj
    const teamIdForQuery = teamId ?? currentTeamId
    const endpointWithTeamId = getEndpointWithTeamId(endpoint, teamIdForQuery)
    const queryKey = queryObj.options?.queryKey ?? [endpointWithTeamId]

    const fetchData = async () =>
      await fetchQueryData(endpointWithTeamId, axiosConfig)

    // force enabled to be a boolean
    // otherwise `undefined` and other falsy values won't disable the query
    // https://github.com/TanStack/query/issues/2269
    if ('enabled' in options) {
      options.enabled = Boolean(options.enabled)
    }

    return {
      queryFn: fetchData,
      queryKey,
      ...options
    }
  })

  const fetchStates = useQueries(queriesMapped)

  return useQueriesResultMemo(fetchStates)
}
